/**
 * One block of a core standard's minimum requirement. A block is an optional
 * lead-in sentence followed by optional bullets. Where a standard lists several
 * blocks they are all required — the source checklist joins them with "AND",
 * and the tooltip renders that separator between them.
 */
export interface PseaMinimumRequirementBlock {
  text?: string;
  items?: string[];
}

export interface PseaCoreStandard {
  number: number;
  title: string;
  description?: string;
  /**
   * What the reviewer must actually see to score the standard, transcribed from
   * the "Minimum Requirement" column of the Scoring Checklist Form (Annex 3).
   * Surfaced as a hover tooltip beside the description on the assessment form.
   */
  minimumRequirements?: PseaMinimumRequirementBlock[];
  options: string[];
  /**
   * Whether "Not applicable" is offered alongside Yes/No. Only standards that
   * can genuinely not apply to an organization carry it — sub-contracting
   * clauses (2) and corrective action for allegations (8), which are moot for
   * an organization that sub-contracts nothing or has had no allegations.
   * It scores the same point as "Yes" so a standard that cannot apply does not
   * drag the rating down.
   */
  allowNotApplicable?: boolean;
}

export const OTHER_SPECIFY_OPTION = "Other (please specify)";

/**
 * The 8 PSEA Core Standards used to structure the PSEA Assessment form and
 * read-only views. Each standard has a title, a descriptive prompt and a list
 * of evidence options (checkboxes). Where "Other (please specify)" is present a
 * free-text field is shown for the respondent to elaborate.
 */
export const PSEA_CORE_STANDARDS: PseaCoreStandard[] = [
  {
    number: 1,
    title: "Organizational Policy",
    description:
      "The partner has a policy document on PSEA. At a minimum, this document should include a written undertaking that the partner accepts the standards of conduct listed in section 3 of the ST/SGB/2003/13",
    minimumRequirements: [
      {
        text: "There is an organizational policy document on PSEA which includes, at minimum, the following:",
        items: [
          "Definitions aligned with those of the United Nations (ST/SGB/2003/13, 2003)",
          "An explicit prohibition of SEA / zero tolerance towards SEA always",
          "Reporting obligations: the requirement that all allegations of SEA must be promptly reported",
        ],
      },
    ],
    options: [
      "Code of Conduct (internal or interagency)",
      "PSEA policy",
      "Documentation of standard procedures for all personnel to receive/sign PSEA policy",
      OTHER_SPECIFY_OPTION,
    ],
  },
  {
    number: 2,
    title: "Organizational Management",
    description:
      "Your organization’s contracts and partnership agreements include a standard clause requiring sub-contractors to adopt policies that prohibit SEA and to take measures to prevent and respond to SEA.",
    minimumRequirements: [
      {
        text: "A standard clause on PSEA is included in all partnership and subcontracting agreements/contracts and/or partnership agreements. At minimum, such clause must include:",
        items: [
          "Clear prohibition of SEA / declaration of zero tolerance to SEA",
          "Definition of SEA",
          "Obligation to inform the organisation upon reception of SEA allegations",
          "The obligation to take all necessary actions to prevent SEA and to take appropriate corrective action when a SEA incident occurs.",
        ],
      },
    ],
    options: [
      "PSEA Contracts/partnership agreements for sub-contractors",
      OTHER_SPECIFY_OPTION,
    ],
    allowNotApplicable: true,
  },
  {
    number: 3,
    title: "Human Resource Systems",
    description:
      "There is a systematic vetting procedure in place for job candidates through proper screening. This must include, at minimum, reference checks for sexual misconduct and a self-declaration by the job candidate, confirming that they have never been subject to sanctions (disciplinary, administrative or criminal) arising from an investigation in relation to SEA, or left employment pending investigation and refused to cooperate in such an investigation.",
    minimumRequirements: [
      {
        text: "The following documents MUST be present:",
        items: [
          "Reference check form (or other reference check evidence) that includes SEA",
          "Self-declaration by the job candidate requesting that they confirm that they have never been subject to sanctions (disciplinary, administrative or criminal) arising from an investigation in relation to SEA, or left employment pending investigation and refused to cooperate in such an investigation. This declaration must also include consent of disclosure of any such information by former employers during the verification of references.",
          "All contracts (staff, volunteer, intern, individual consultant) signed include a PSEA clause that lays out specific PSEA obligations. A Code of Conduct may be accepted instead of the PSEA clause, as long as it integrates specific PSEA obligations.",
        ],
      },
    ],
    options: [
      "Reference check template including check for sexual misconduct (including reference from previous employers and self-declaration)",
      "Recruitment procedures",
      OTHER_SPECIFY_OPTION,
    ],
  },
  {
    number: 4,
    title: "Mandatory Training",
    description:
      // Line breaks are meaningful here: the three requirements are a list in
      // the source document, and the description is rendered with pre-wrap so
      // they stay on separate lines.
      "The partner holds mandatory trainings (online or in-person) for all IP employees and associated personnel (herein \u201cpersonnel\u201d) on PSEA and relevant procedures.\n" +
      "The training should, at a minimum include:\n" +
      "1) a definition of SEA (that is aligned with the UN's definition);\n" +
      "2) an explanation on prohibition of SEA; and\n" +
      "3) actions that personnel are required to take (i.e. prompt reporting of allegations and referral of victims).",
    minimumRequirements: [
      {
        text: "A policy/procedural document is present (PSEA policy, training policy/plan, HR manual, SOP) requiring mandatory training on PSEA both during the onboarding process and on a regular basis (ideally once a year) for all personnel (e.g., employees/staff, consultants, contractors, volunteers, interns etc.)",
      },
      {
        text: "Training materials are available and must include:",
        items: [
          "A clear definition of SEA (same as or substantively similar to the UN's definition);",
          "An explicit prohibition of SEA;",
          "The requirement that any allegations of SEA must be promptly reported;",
          "The presentation of available reporting channels accessible to staff and beneficiaries",
          "The requirement that alleged victims of SEA shall be promptly referred for immediate, professional assistance.",
        ],
      },
    ],
    options: [
      "Annual training plan",
      "Training agenda",
      "Training package",
      "Attendance sheets",
      "Training certificates",
      OTHER_SPECIFY_OPTION,
    ],
  },
  {
    number: 5,
    title: "Reporting",
    description:
      "The partner has mechanisms and procedures for personnel, recipients of assistance and communities, including children, to report SEA allegations that comply with core standards for reporting (i.e. safety, confidentiality, transparency, accessibility).",
    minimumRequirements: [
      {
        items: [
          "A mechanism is available and accessible for personnel and beneficiaries to report SEA or safeguarding violations against children and adults. Such mechanisms must be in line with core reporting standards (i.e., accessibility, responsiveness, safety, confidentiality and transparency).",
        ],
      },
      {
        items: [
          "The organization is actively raising awareness with beneficiaries about the reporting mechanism, that can be evidenced through communication material and awareness raising plan.",
        ],
      },
    ],
    options: [
      "Internal Complaints and Feedback Mechanism",
      "Participation in joint reporting mechanisms",
      "Communication materials",
      "PSEA awareness raising plan",
      "Description of reporting mechanism",
      "Whistle-blower policy",
      OTHER_SPECIFY_OPTION,
    ],
  },
  {
    number: 6,
    title: "Assistance and Referrals",
    description:
      "The partner has a system to refer SEA victims to locally available support services, based on their needs and consent. This can include actively contributing to in-country PSEA networks and/or GBV systems (where applicable) and/or referral pathways at an inter-agency level.",
    minimumRequirements: [
      {
        items: [
          "The organizational policy document on PSEA, or a guidance, or standard operating procedures (SOP) on handling SEA allegations, lays out the requirement to systematically provide victims of SEA with any assistance required — either directly or through a referral — based on their needs and consents (victim-centered approach).",
        ],
      },
      {
        items: [
          "There is evidence that a referral mechanism/procedure exists, such as mapping/list of service providers and guidance on referral process.",
        ],
      },
    ],
    options: [
      "Internal or Interagency referral pathway",
      "List of available service providers",
      "Description of referral procedures or Standard Operation Procedure (SOP)",
      "Referral form for survivors/victims of GBV or SEA",
      "Guidelines on victim assistance and/or training on GBV and GBV case management principles",
    ],
  },
  {
    number: 7,
    title: "Investigations",
    description:
      "The organization has a process for investigation of allegations of SEA and can provide evidence. This may include a referral system for investigations where in-house capacity does not exist.",
    minimumRequirements: [
      {
        text: "An internal procedure exists with timelines and milestones, and include the following:",
        items: [
          "Process to review received reported cases",
          "Process of investigation",
          "Information on how to ensure confidentiality in the investigation process.",
          "The list of investigation follow-up actions (disciplinary actions, information sharing, etc.). Disciplinary actions should be described in the HR procedures/policies.",
        ],
      },
      {
        items: [
          "There is evidence of dedicated investigation capacity (composition of investigation team, dedicated trained employees, contract with a specialized investigation services).",
        ],
      },
    ],
    options: [
      "Written process for review of SEA allegations",
      "Dedicated resources for investigation(s) and/or commitment of partner for support",
      "PSEA investigation policy/procedures",
      "Contract with professional investigative service",
      OTHER_SPECIFY_OPTION,
    ],
  },
  {
    number: 8,
    title: "Corrective Action",
    description:
      "The partner has taken appropriate corrective action in response to SEA allegations, if any.",
    minimumRequirements: [
      {
        text: "Adequate corrective measures were undertaken in relation to any past SEA allegation. Below is a non-exhaustive list of factors that may be considered in making this determination:",
        items: [
          "The partner has promptly informed UN of the SEA allegation.",
          "A thorough and transparent investigation of the SEA allegation involving the partner has been undertaken.",
          "The outcome of the investigation/s on the SEA allegation have been shared with the UN.",
          "Appropriate action has been taken towards the perpetrator, if the allegation was found to be substantiated.",
          "Adequate assistance and protection have been provided to the (alleged) victim/s of SEA;",
          "A time-bound action plan has been implemented to address systemic issues and key gaps.",
        ],
      },
    ],
    options: [
      "Evidence of implementation of corrective measures identified by the UN partner entity, including capacity strengthening of staff",
      "Specific measures to identify and reduce risks of SEA in programme delivery",
      OTHER_SPECIFY_OPTION,
    ],
    allowNotApplicable: true,
  },
];
